import Vue from 'vue'
import axios from 'axios'

/**
 * Whether the platform can be used right now (BGT-8224).
 *
 * The frontends are static files on CloudFront, so they keep loading when the API runs no tasks. Without
 * this, V2 showed the welcome screen with a generic error toast. Two signals decide the state:
 * - `/maintenance.json` in the frontend bucket, placed there by hand before taking the API down, with an
 *   optional message per locale;
 * - `/api/up`, Laravel's health route, which answers 200 whenever PHP runs, so a 502/503/504 or no answer at
 *   all means the API itself is unreachable.
 */
export type AvailabilityState = 'ok' | 'unavailable' | 'maintenance'

export interface MaintenanceNotice {
	maintenance: true
	message?: Record<string, string>
}

const GATEWAY_STATUSES = new Set([502, 503, 504])
export const FAILURE_THRESHOLD = 3
export const RECHECK_INTERVAL_MS = 30000
// an endpoint that takes the connection but never answers must not keep the app from mounting
export const CHECK_TIMEOUT_MS = 8000

export const availability = Vue.observable({
	state: 'ok' as AvailabilityState,
	message: null as Record<string, string> | null
})

let consecutiveFailures = 0
let confirming = false
let recheckTimer: ReturnType<typeof setInterval> | null = null

/** No timeout on a browser without `AbortSignal.timeout`: a missing API must not read as an outage. */
function timeoutSignal(): AbortSignal | undefined {
	// not in this TypeScript lib yet
	const signals = typeof AbortSignal !== 'undefined' ? (AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal }) : null

	return typeof signals?.timeout === 'function' ? signals.timeout(CHECK_TIMEOUT_MS) : undefined
}

function apiBase(): URL {
	return new URL(process.env.VUE_APP_API_URL || '/api/v2/', window.location.href)
}

/** `/api/up` next to the versioned API base, on whatever host that base points at. */
export function statusUrl(): string {
	return new URL('../up', apiBase()).toString()
}

/** Only our own API counts: a third party being down says nothing about the platform. */
export function isOwnApiRequest(url: string | undefined): boolean {
	if (!url) {
		return false
	}
	const base = apiBase()
	const target = new URL(url, base)

	return target.origin === base.origin && target.pathname.startsWith('/api/')
}

export function isGatewayFailure(error: any): boolean {
	if (!error || axios.isCancel(error)) {
		return false
	}
	if (!error.response) {
		return true
	}

	return GATEWAY_STATUSES.has(error.response.status)
}

export function parseMaintenanceNotice(data: unknown): MaintenanceNotice | null {
	if (!data || typeof data !== 'object' || (data as any).maintenance !== true) {
		return null
	}
	const message = (data as any).message

	return {
		maintenance: true,
		message: message && typeof message === 'object' ? message : undefined
	}
}

/** The message for the locale, falling back to Dutch and then English. */
export function maintenanceMessage(message: Record<string, string> | null, locale: string): string | null {
	if (!message) {
		return null
	}

	return message[locale] || message.nl || message.en || null
}

export async function fetchMaintenanceNotice(): Promise<MaintenanceNotice | null> {
	try {
		const response = await fetch(`/maintenance.json?t=${Date.now()}`, { cache: 'no-store', signal: timeoutSignal() })
		if (!response.ok) {
			return null
		}

		// a missing file can come back as the index.html fallback, which is not JSON
		return parseMaintenanceNotice(await response.json())
	} catch {
		// unreachable, timed out or not JSON: no maintenance notice to show
		return null
	}
}

export async function isApiDown(): Promise<boolean> {
	try {
		const response = await fetch(statusUrl(), {
			cache: 'no-store',
			credentials: 'omit',
			signal: timeoutSignal()
		})

		// anything but a gateway error means PHP answered: a 404 (not deployed yet) or a 500 is not an outage
		return GATEWAY_STATUSES.has(response.status)
	} catch {
		// no answer at all; when the browser itself is offline that is the user's connection, not ours
		return navigator.onLine !== false
	}
}

export async function evaluate(): Promise<AvailabilityState> {
	const [notice, down] = await Promise.all([fetchMaintenanceNotice(), isApiDown()])

	if (notice) {
		availability.state = 'maintenance'
		availability.message = notice.message ?? null
	} else if (down) {
		availability.state = 'unavailable'
		availability.message = null
	} else {
		availability.state = 'ok'
		availability.message = null
	}

	return availability.state
}

function startRecheck(): void {
	if (recheckTimer) {
		return
	}
	recheckTimer = setInterval(async () => {
		if ((await evaluate()) === 'ok') {
			window.location.reload()
		}
	}, RECHECK_INTERVAL_MS)
}

/** Decides the state before the app mounts. Never rejects: a failing check must not block the app. */
export async function boot(): Promise<AvailabilityState> {
	try {
		if ((await evaluate()) !== 'ok') {
			startRecheck()
		}
	} catch {
		availability.state = 'ok'
	}

	return availability.state
}

export function reportSuccess(): void {
	consecutiveFailures = 0
}

/** A run of gateway errors on our API while working: confirm with `/api/up` before switching. */
export async function reportFailure(error: any): Promise<void> {
	if (availability.state !== 'ok' || !isGatewayFailure(error) || !isOwnApiRequest(error?.config?.url)) {
		return
	}
	consecutiveFailures++
	if (consecutiveFailures < FAILURE_THRESHOLD) {
		return
	}

	await confirmOutage()
}

/** Also used when the embedded V3 app reports the API as unreachable. */
export async function confirmOutage(): Promise<void> {
	if (confirming || availability.state !== 'ok') {
		return
	}
	confirming = true
	try {
		if ((await evaluate()) !== 'ok') {
			startRecheck()
		} else {
			consecutiveFailures = 0
		}
	} finally {
		confirming = false
	}
}

/** Error toasts would only repeat what the unavailable page already says. */
export function isSuppressingErrors(): boolean {
	return availability.state !== 'ok'
}

/** Test helper: back to a clean state between specs. */
export function resetAvailability(): void {
	availability.state = 'ok'
	availability.message = null
	consecutiveFailures = 0
	confirming = false
	if (recheckTimer) {
		clearInterval(recheckTimer)
		recheckTimer = null
	}
}
